import React from "react";
import {
  Box,
  Icon,
  PreviewCardFacts,
  PreviewCardFactsSkeleton,
  PreviewCardFooter,
  PreviewCardFrame,
  PreviewCardSeparator,
  Text,
} from "@salestrak/ui";

import { formatPhoneNumber } from "@salestrak/custom-fields";

import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import {
  entityFromAssigneeRef,
  memberStatusBadge,
  resolveEntityLabel,
  teamRefToEntity,
  userRefToEntity,
  type EntityData,
} from "#app/lib/entity-visuals";
import { useIssueById } from "#app/lib/hooks/use-issue-by-id";
import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { formatAge } from "#app/lib/issue-utils";
import { formatTime } from "#app/lib/utils/format-date";
import {
  presenceDotProps,
  usePresence,
} from "#app/lib/data/presence-provider";
import { pluralize } from "#app/components/issue-view/activity/activity-utils";

import { DetailFieldRow } from "./detail-field-row";
import { useEntityDetailOptional } from "./entity-detail-provider";
import { EntityVisual } from "./entity-visual";
import { EntityGroupTag } from "./entity-group";
import { EntityTag } from "./entity-tag";
import { IdentityHeader } from "./preview-card";
import { formatTeamHandle } from "#app/lib/team-handle";
import type { Omittable } from "@salestrak/ui";
import { memberContact } from "./member-contact";

// Hover-card content, mounted only inside an open popover so one lazy hook fires
// per open. Fetching kinds use `cache-first` so pointer travel never sprays
// network.

/**
 * The kinds that carry a hover card. Stage, Workflow, Project Type and Source
 * chips name a workspace vocabulary the reader already knows; a card on them
 * is noise over every property row and picker value, so they have none — the
 * chip's click (detail dialog, where one exists) is their whole interaction.
 */
const PREVIEW_KINDS: ReadonlySet<EntityData["kind"]> = new Set([
  "user",
  "team",
  "issue",
]);

export function hasEntityPreview(entity: EntityData): boolean {
  return PREVIEW_KINDS.has(entity.kind);
}

export interface EntityPreviewProps {
  entity: EntityData;
  /** Called by any preview action that takes the user somewhere (View
   *  profile, Go to team, Open in Map) — the hosting hover card passes its
   *  `close` here so it never lingers over the destination. */
  onNavigate?: () => void;
}

export function EntityPreview({ entity, onNavigate }: EntityPreviewProps) {
  switch (entity.kind) {
    case "user":
      return <UserPreview entity={entity} onNavigate={onNavigate} />;
    case "team":
      return <TeamPreview entity={entity} onNavigate={onNavigate} />;
    case "stage":
    case "workflow":
    case "issueType":
    case "issueSource":
      // No card for these kinds — `hasEntityPreview` keeps their chips from
      // arming hover at all, so this branch is the type-level backstop.
      return null;
    case "issue":
      return <IssuePreview entity={entity} onNavigate={onNavigate} />;
  }
}

function teamUsersToEntities(
  users:
    | ReadonlyArray<{ userId: string; name?: string | null; profileImage?: string | null }>
    | null
    | undefined,
): EntityData[] {
  return (users ?? []).map(userRefToEntity);
}

function UserPreview({
  entity,
  onNavigate,
}: Omittable<{
  entity: Extract<EntityData, { kind: "user" }>;
  onNavigate?: () => void;
}>) {
  const { user, member, loading } = useMemberDetail(entity.id, {
    fetchPolicy: "cache-first",
  });
  const detail = useEntityDetailOptional();
  const { push } = useAppNavigate();

  const teams = member?.teams ?? [];
  const initialLoading = loading && !user;
  const email = user?.email ?? entity.user?.email ?? null;

  // The status row: live presence when the account is active; non-active
  // accounts (invited, suspended) keep their account-status label — "online"
  // is meaningless for someone who has never signed in.
  const account = memberStatusBadge(user?.memberStatus ?? entity.user?.memberStatus);
  const presence = usePresence(entity.id);
  const live = presence ? presenceDotProps(presence).status : "offline";
  const status =
    account.label !== "Online"
      ? account
      : live === "online"
        ? ({ label: "Online", color: "success" } as const)
        : live === "away"
          ? ({ label: "Away", color: "warning" } as const)
          : ({ label: "Offline", color: "muted" } as const);

  return (
    <PreviewCardFrame>
      <IdentityHeader
        entity={entity}
        title={user?.name || resolveEntityLabel(entity)}
        subtitle={user?.title ?? null}
        presence
      />

      <PreviewCardSeparator />

      {initialLoading ? (
        // Status, email, local time, teams — the rows a member card settles
        // to (a phone row is the rare fifth).
        <PreviewCardFactsSkeleton rows={4} />
      ) : (
        <PreviewCardFacts>
          {/* Always the wifi glyph — the row's ROW is the status signal; the
           * value text carries online/away/offline. */}
          <DetailFieldRow hideLabel compact icon="wifi" label="Status">
            <Text
              size="xs"
              variant={status.color === "success" ? "success" : status.color === "warning" ? "warning" : "muted"}
            >
              {status.label}
            </Text>
          </DetailFieldRow>
          {email ? (
            <DetailFieldRow
              hideLabel
              compact
              icon="mail"
              label="Email"
              value={email}
              link="email"
              onContact={memberContact("email", "preview")}
              truncate={1}
            />
          ) : null}
          {user?.phoneNumber ? (
            <DetailFieldRow
              hideLabel
              compact
              icon="phone"
              label="Phone"
              value={formatPhoneNumber(user.phoneNumber)}
              link="phone"
              onContact={memberContact("phone", "preview")}
            />
          ) : null}
          {/* Users carry no timezone yet — this is the viewer's clock, which
           * matches for a single-region workspace. */}
          <DetailFieldRow hideLabel compact icon="clock" label="Local time">
            <Text size="xs" variant="muted">
              {`${formatTime(new Date().toISOString())} local time`}
            </Text>
          </DetailFieldRow>
          <DetailFieldRow hideLabel compact icon="users" label="Teams">
            {/* The entity-value rule: ONE team reads tile + name and opens
             * its page; two or more collapse to the stack, whose tooltip
             * names them — never a comma list that outruns the card. */}
            {teams.length === 0 ? (
              <Text size="xs" variant="muted">
                No teams
              </Text>
            ) : teams.length === 1 ? (
              <Box shrink minWidth={0}>
                <EntityTag
                  entity={teamRefToEntity(teams[0]!)}
                  size="xs"
                  weight="regular"
                  onClick={
                    detail?.canOpenEntity("team", teams[0]!.teamId)
                      ? () => {
                          push(`/team/${teams[0]!.teamId}`);
                          onNavigate?.();
                        }
                      : undefined
                  }
                />
              </Box>
            ) : (
              <EntityGroupTag
                entities={teams.map(teamRefToEntity)}
                variant="stack"
                surface="popover"
                title="Teams"
              />
            )}
          </DetailFieldRow>
        </PreviewCardFacts>
      )}

      {detail?.canOpenEntity("user", entity.id) && !detail.contained ? (
        <PreviewCardFooter
          icon="circle-user"
          label="View profile"
          onClick={() => {
            detail.openEntity("user", entity.id, { via: "preview" });
            onNavigate?.();
          }}
        />
      ) : null}
    </PreviewCardFrame>
  );
}

function TeamPreview({
  entity,
  onNavigate,
}: Omittable<{
  entity: Extract<EntityData, { kind: "team" }>;
  onNavigate?: () => void;
}>) {
  const { team, loading } = useTeamDetail(entity.id, {
    fetchPolicy: "cache-first",
  });
  const detail = useEntityDetailOptional();

  const initialLoading = loading && !team;
  const leadEntities = teamUsersToEntities(team?.leads);
  const memberEntities = teamUsersToEntities(team?.members);
  const totalCount = leadEntities.length + memberEntities.length;
  const isPrivate = team?.private ?? false;

  return (
    <PreviewCardFrame>
      <IdentityHeader
        entity={entity}
        title={team?.teamName ?? entity.team.teamName}
        subtitle={formatTeamHandle(team?.teamKey || entity.team.teamKey) || null}
      />

      <PreviewCardSeparator />

      {initialLoading ? (
        // Visibility, manager, members.
        <PreviewCardFactsSkeleton rows={3} />
      ) : (
        <PreviewCardFacts>
          {/* Visibility — the member card's status-row slot. */}
          <DetailFieldRow
            hideLabel
            compact
            icon={isPrivate ? "lock" : "globe"}
            label="Visibility"
          >
            <Text size="xs" variant="muted">
              {isPrivate ? "Private team" : "Public team"}
            </Text>
          </DetailFieldRow>
          {/* `shield` is the manager glyph everywhere a roster splits by role;
           * the "Managed by" prefix keeps the icon-led row legible. Managers
           * follow the standard single-name/multi-stack rule — never the
           * expanded pill. Teams without a manager skip the row rather than
           * advertising the absence (a manager-less team is valid — workspace
           * admins run it). */}
          {leadEntities.length > 0 ? (
            <DetailFieldRow
              hideLabel
              compact
              icon="shield"
              label={pluralize("Manager", { count: leadEntities.length })}
            >
              <Box direction="row" align="center" gap="xs">
                <Box shrink={false}>
                  <Text size="xs" variant="muted">
                    Managed by
                  </Text>
                </Box>
                {leadEntities.length === 1 ? (
                  <Box shrink minWidth={0}>
                    {/* size="xs" = the 20px person chip on the facts rows'
                     * 12px type rhythm. */}
                    <EntityTag entity={leadEntities[0]!} size="xs" weight="regular" />
                  </Box>
                ) : (
                  <EntityGroupTag
                    entities={leadEntities}
                    variant="stack"
                    surface="popover"
                    title="Managers"
                  />
                )}
              </Box>
            </DetailFieldRow>
          ) : null}
          <DetailFieldRow hideLabel compact icon="users" label="Members">
            <Text size="xs" variant="muted">
              {totalCount > 0
                ? `${totalCount} ${pluralize("member", { count: totalCount })}`
                : // The roster resolvers return empty for a private team the
                  // viewer can't see into — say so instead of "no members".
                  isPrivate
                  ? "Members visible to team members"
                  : "No members yet"}
            </Text>
          </DetailFieldRow>
        </PreviewCardFacts>
      )}

      {/* The team's detail is its page. A team outside the viewer's list has
       * no page they can reach, so the footer is withheld rather than sending
       * them into Access Denied. */}
      {detail?.canOpenEntity("team", entity.id) ? (
        <PreviewCardFooter
          icon="users"
          label="View Team"
          onClick={() => {
            detail.openEntity("team", entity.id);
            onNavigate?.();
          }}
        />
      ) : null}
    </PreviewCardFrame>
  );
}

function IssuePreview({
  entity,
  onNavigate,
}: Omittable<{
  entity: Extract<EntityData, { kind: "issue" }>;
  onNavigate?: () => void;
}>) {
  const { stageMap, workflowMap } = useWorkspaceData();
  const { issue, loading } = useIssueById(entity.id);
  const detail = useEntityDetailOptional();
  const initialLoading = loading && !issue;
  const stage = issue?.stageId ? stageMap.get(issue.stageId) : undefined;
  const workflow = issue?.workflowId ? workflowMap.get(issue.workflowId) : undefined;
  const ageStatus = issue?.age?.status
    ? AGE_STATUS_CONFIG[issue.age.status as AgeStatus]
    : undefined;
  const ageLabel = formatAge(issue?.age?.date);
  const address = issue?.address?.wholeAddress;

  const assigneeEntities = React.useMemo(
    () => (issue?.assignees ?? []).map(entityFromAssigneeRef),
    [issue?.assignees],
  );

  // A project has no identity mark of its own, so the header is text alone:
  // the name, then Type · Workflow. The stage is a FACT — its pie in the
  // glyph slot and its name as plain text, one glyph per row.
  const subtitle =
    [issue?.issueType?.name, workflow?.name].filter(Boolean).join(" · ") || null;

  return (
    <PreviewCardFrame>
      <IdentityHeader
        entity={entity}
        title={issue?.name ?? resolveEntityLabel(entity)}
        subtitle={subtitle}
        visual={null}
      />

      <PreviewCardSeparator />

      {initialLoading ? (
        // Stage, follow up, assignees, address.
        <PreviewCardFactsSkeleton rows={4} />
      ) : (
        <PreviewCardFacts>
          {stage ? (
            <DetailFieldRow
              hideLabel
              compact
              label="Stage"
              leading={
                <EntityVisual
                  entity={{ kind: "stage", id: stage.stageId, stage }}
                  size="xs"
                />
              }
            >
              <Text overflowTooltip size="xs" truncate={1}>
                {stage.name}
              </Text>
            </DetailFieldRow>
          ) : null}
          {ageLabel && ageStatus ? (
            // The follow-up status wears its pulse — the one filled glyph for
            // Overdue — and the text sits on the same hue (the age-label pair).
            <DetailFieldRow
              hideLabel
              compact
              label="Follow up"
              leading={
                <Icon name={ageStatus.icon} size="sm" color={ageStatus.decoColor} />
              }
            >
              <Text size="xs" color={ageStatus.decoColor}>
                {`${ageStatus.label} · ${ageLabel}`}
              </Text>
            </DetailFieldRow>
          ) : null}
          {assigneeEntities.length > 0 ? (
            <DetailFieldRow
              hideLabel
              compact
              icon="user"
              label={pluralize("Assignee", { count: assigneeEntities.length })}
            >
              {/* The entity-value rule: one assignee reads avatar + name,
               * two or more collapse to the label-less stack. */}
              {assigneeEntities.length === 1 ? (
                <Box shrink minWidth={0}>
                  <EntityTag entity={assigneeEntities[0]!} size="xs" weight="regular" />
                </Box>
              ) : (
                <EntityGroupTag
                  entities={assigneeEntities}
                  variant="stack"
                  surface="popover"
                  title="Assignees"
                />
              )}
            </DetailFieldRow>
          ) : null}
          {address ? (
            <DetailFieldRow
              hideLabel
              compact
              align="start"
              icon="map-pin"
              label="Location"
            >
              <Text overflowTooltip size="xs" truncate={2}>
                {address}
              </Text>
            </DetailFieldRow>
          ) : null}
        </PreviewCardFacts>
      )}

      {detail?.canOpenEntity("issue", entity.id) ? (
        <PreviewCardFooter
          icon="home"
          label="View Project"
          onClick={() => {
            detail.openEntity("issue", entity.id);
            onNavigate?.();
          }}
        />
      ) : null}
    </PreviewCardFrame>
  );
}
